import {
  GameType,
  TrackingPageGameBlock,
} from "@redotech/order-tracking-common/tracking-page-definition";
import { useEffect, useState } from "react";
import { CustomSprite, GameState } from "../game";
import { GameControls } from "../game-controls";
import { preprocessSpriteForAlignment } from "../util/preprocess-sprite";

export function useDynamicSpriteUpdates(
  blockProps: TrackingPageGameBlock,
  gameState: GameState,
  gameControls: GameControls | null,
) {
  const [currentPlayerSprite, setCurrentPlayerSprite] =
    useState<CustomSprite | null>(null);
  const [currentNpcSprite, setCurrentNpcSprite] = useState<CustomSprite | null>(
    null,
  );

  // Extract complex expressions for dependency array
  const isTinyPackage = blockProps.gameType === GameType.TINY_PACKAGE;
  const playerSpriteUrl = isTinyPackage
    ? blockProps.gameSettings?.playerSpriteUrl
    : null;
  const playerSpriteSize = isTinyPackage
    ? blockProps.gameSettings?.playerSpriteSize
    : null;
  const npcSpriteUrl = isTinyPackage
    ? blockProps.gameSettings?.npcSpriteUrl
    : null;
  const npcSpriteSize = isTinyPackage
    ? blockProps.gameSettings?.npcSpriteSize
    : null;

  useEffect(() => {
    if (
      gameState === GameState.Gameplay &&
      blockProps.gameType === GameType.TINY_PACKAGE &&
      blockProps.gameSettings &&
      gameControls?.updateSprites
    ) {
      const updateGameSprites = async () => {
        const playerSpriteUrl = blockProps.gameSettings.playerSpriteUrl;
        const playerSpriteSize = blockProps.gameSettings.playerSpriteSize || 32;
        const npcSpriteUrl = blockProps.gameSettings.npcSpriteUrl;
        const npcSpriteSize = blockProps.gameSettings.npcSpriteSize || 32;

        const processSpriteIfNeeded = async (
          spriteUrl: string | undefined | null,
          spriteSize: number,
          currentSprite: CustomSprite | null,
          defaultUrl: string,
          setter: (sprite: CustomSprite) => void,
        ): Promise<CustomSprite | null> => {
          const currentSize = currentSprite ? spriteSize : null;
          const currentUrl = currentSprite?.url;

          if (spriteUrl !== currentUrl || spriteSize !== currentSize) {
            let newSprite: CustomSprite;

            if (spriteUrl) {
              try {
                newSprite = await preprocessSpriteForAlignment(
                  spriteUrl,
                  spriteSize,
                );
              } catch (error) {
                console.error("Failed to preprocess sprite:", error);
                newSprite = { url: spriteUrl, verticalOffset: 0 };
              }
            } else {
              newSprite = { url: defaultUrl, verticalOffset: 0 };
            }

            setter(newSprite);
            return newSprite;
          }
          return null;
        };

        const [newPlayerSprite, newNpcSprite] = await Promise.all([
          processSpriteIfNeeded(
            playerSpriteUrl,
            playerSpriteSize,
            currentPlayerSprite,
            "https://play.kaplayjs.com/sprites/bean.png",
            setCurrentPlayerSprite,
          ),
          processSpriteIfNeeded(
            npcSpriteUrl,
            npcSpriteSize,
            currentNpcSprite,
            "https://play.kaplayjs.com/sprites/mark.png",
            setCurrentNpcSprite,
          ),
        ]);

        if ((newPlayerSprite || newNpcSprite) && gameControls.updateSprites) {
          gameControls.updateSprites(
            newPlayerSprite || currentPlayerSprite,
            newNpcSprite || currentNpcSprite,
            playerSpriteSize,
            npcSpriteSize,
          );
        }
      };

      updateGameSprites().catch((error) => {
        console.error("Dynamic sprite update failed:", error);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerSpriteUrl, playerSpriteSize, npcSpriteUrl, npcSpriteSize]);

  return { setCurrentPlayerSprite, setCurrentNpcSprite };
}
